import { ChangeEvent, createContext, ReactNode, useEffect, useId, useState } from "react";
import { ApolloClient, NormalizedCacheObject, useApolloClient } from "@apollo/client";
import getConfig from "next/config";
import { useRouter } from "next/router";
import axios, { AxiosResponse } from "axios";
import { toast, TypeOptions } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import gql from "graphql-tag";
import guid from "@pinnacle/utils/src/utils/getUuid";
import { CookieValueTypes, deleteCookie, getCookie } from "cookies-next";
import { ShareMockData } from "../shared/MockData/ShareMockData";
import { ArcUser, verifyArcSession } from "services/haywire/verifyArcSession";
import { userPreferences } from "../services/haywire/userPreferences";
import getParagraphInfo from "../services/queries/utils/getParagraphInfo";
import { formatTimeToCustomVersion } from "../shared/utils/formatTime";
import { setCookie } from "../shared/utils/setCookie";
import {
	BettingOddsTypeInterface,
	GetAllFeaturedSportsInterface,
	GetAllSportsLeaguesInterface,
	HaywireUrlsInterface,
	LanguageMapInterface,
	SocialItemInterface,
	SportItemInterface,
	SportsDataInterface,
} from "../shared/Interfaces/globalContextInterface";
import styles from "../common/MainPage/style.module.scss";
import { oddsTypes } from "../shared/constants/oddsTypes";

interface IGlobalContextProps {
	boxesBottom: [];
	sidebar: [];
	language: string;
	brSocial: SocialItemInterface[] | null;
	modalOpen: boolean;
	spotlightCheck: boolean;
	autoPlayVideo: boolean;
	reset: boolean;
	isDarkMode: boolean;
	CSRLoader: boolean;
	userInfo: ArcUser | null;
	cookieBannerUUID: string | null;
	isPhone: boolean;
	isComp: boolean;
	haywireUrls: HaywireUrlsInterface | null;
	getTime: string;
	getAllFeaturedSports: GetAllFeaturedSportsInterface[] | null;
	getAllSportsLeagues: GetAllSportsLeaguesInterface[] | null;
	getAllSportsData: GetAllFeaturedSportsInterface[] | null;
	dateFormats: LanguageMapInterface | null;
	predictionNId: number | null;
	headerLogo: string | undefined;
	selectorOddsData: BettingOddsTypeInterface[];
	selectorOddsDataActive: BettingOddsTypeInterface | null | undefined;
	toggle: boolean;
	deviceUuid: string | undefined;
	apolloInit: ApolloClient<NormalizedCacheObject>;
	randomId: string;
	trueLang: string;
	getSportsDataLoader: boolean;
	getErrorPageURL: string;
	tld: string;
	getPredictionsPageCode: string;
	langCode: string;
	textFontSize: number | null;
	headingsList: any[] | null;
	activeHeadingItem: string | null;
	handleGlobalReset: any;
	handleModal: (bool: boolean) => void;
	handleAutoPlayVideo: () => void;
	handleToast: (status: TypeOptions | undefined | "status", message: string) => void;
	handleCSR: (uuid: string | undefined, type: string) => any;
	handleRemoveSession: () => void;
	handleClickChangeOddsType: (id: number) => void;
	handleToggle: (bool: boolean) => void;
	handleSpotLight: () => void;
	handleGetSports: () => void;
	handleBaseHaywireUrl: (url: string, params: object) => any;
	handleTextFontSize: (fontsize: number) => void;
	handleHeadingsList: (item: any) => void;
	handleActiveHeadingItem: (item: any) => void;
}

export const GlobalContext = createContext<IGlobalContextProps>({
	boxesBottom: [],
	sidebar: [],
	language: "",
	brSocial: null,
	modalOpen: false,
	spotlightCheck: false,
	autoPlayVideo: false,
	reset: false,
	isDarkMode: false,
	CSRLoader: false,
	userInfo: null,
	cookieBannerUUID: null,
	isPhone: false,
	isComp: false,
	haywireUrls: null,
	getTime: "",
	getAllFeaturedSports: null,
	getAllSportsLeagues: null,
	getAllSportsData: null,
	dateFormats: null,
	predictionNId: null,
	headerLogo: "",
	selectorOddsData: oddsTypes,
	selectorOddsDataActive: null,
	toggle: false,
	deviceUuid: "",
	// Default value is never used in practice — every consumer is mounted under
	// <GlobalContextProvider>, which supplies the real ApolloClient via
	// useApolloClient(). Avoid eagerly constructing a client at module load.
	apolloInit: null as unknown as ApolloClient<NormalizedCacheObject>,
	randomId: "",
	trueLang: "en",
	getSportsDataLoader: true,
	getErrorPageURL: "",
	tld: "",
	getPredictionsPageCode: "",
	langCode: "",
	textFontSize: null,
	headingsList: null,
	activeHeadingItem: "",
	handleGlobalReset: () => {},
	handleModal: () => {},
	handleAutoPlayVideo: () => {},
	handleToast: () => {},
	handleCSR: () => {},
	handleRemoveSession: () => {},
	handleClickChangeOddsType: () => {},
	handleToggle: () => {},
	handleSpotLight: () => {},
	handleGetSports: () => {},
	handleBaseHaywireUrl: () => {},
	handleTextFontSize: () => {},
	handleHeadingsList: () => {},
	handleActiveHeadingItem: () => {},
});

axios.defaults.headers.common = {
	"Content-Type": "application/json",
	"Accept": "application/json",
};

export const GlobalContextProvider = ({ children, ssrData }: { children: ReactNode; ssrData: any }) => {
	const { boxesBottom, sidebar, language, data, tld } = ssrData;
	const router = useRouter();
	const { publicRuntimeConfig } = getConfig();
	const randomId: string = useId();
	const isPhone = useMediaQuery({ query: "(max-width: 767px)" });
	const isComp = useMediaQuery({ query: "(max-width: 1024px" });
	const deviceUuid = `${guid()}-${guid()}-${guid()}-${guid()}`;
	// Use the single ApolloClient provided by <ApolloProvider> in _app.page.tsx.
	// Previously this called initializeApollo(...) on every render, allocating a
	// fresh ApolloClient + InMemoryCache + link chain each time — the main
	// source of the server-side memory leak.
	const apolloInit = useApolloClient() as ApolloClient<NormalizedCacheObject>;

	const [brSocial, setBrSocial] = useState<SocialItemInterface[] | null>(null);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [spotlightCheck, setSpotlightCheck] = useState<boolean>(false);
	const [reset, setReset] = useState<boolean>(false);
	const [autoPlayVideo, setAutoPlayVideo] = useState<boolean>(false);
	const [isDarkMode /*setIsDarkMode*/] = useState<boolean>(false);
	const [userInfo, setUserInfo] = useState<ArcUser | null>(null);
	const [CSRLoader, setCSRLoader] = useState<boolean>(false);
	const [cookieBannerUUID, setCookieBannerUUID] = useState<string | null>(null);
	const [haywireUrls, setHaywireUrls] = useState<HaywireUrlsInterface | null>(null);
	const [getTime, setGetTime] = useState<string>("");
	const [getSportsDataLoader, setSportsDataLoader] = useState<boolean>(true);
	const [sportsData, setSportsData] = useState<SportsDataInterface | null>(null);
	const [getAllSportsData, setGetAllSportsData] = useState<GetAllFeaturedSportsInterface[] | null>(null);
	const [getAllFeaturedSports, setGetAllFeaturedSports] = useState<GetAllFeaturedSportsInterface[] | null>(null);
	const [getAllSportsLeagues, setGetAllSportsLeagues] = useState<GetAllSportsLeaguesInterface[] | null>(null);
	const [dateFormats, setDateFormats] = useState<LanguageMapInterface | null>(null);
	const [predictionNId, setPredictionNId] = useState<number | null>(null);
	const [headerLogo, setHeaderLogo] = useState<string | undefined>("");
	const [toggle, setToggle] = useState(false);
	const [selectorOddsData, setSelectorOddsData] = useState<BettingOddsTypeInterface[]>(oddsTypes);
	const [selectorOddsDataActive, setSelectorOddsDataActive] = useState<BettingOddsTypeInterface | null | undefined>(
		null,
	);
	const [trueLang, setTrueLang] = useState<string>("en");
	const [getUserPrefsCookie, setGetUserPrefsCookie] = useState<CookieValueTypes | string[]>("");
	const [getErrorPageURL, setGetErrorPageURL] = useState<string>("");
	const [getPredictionsPageCode, setGetPredictionsPageCode] = useState<string>("");
	const [langCode, setLangCode] = useState<string>(language);
	const [textFontSize, setTextFontSize] = useState<number | null>(null);
	const [headingsList, setHeadingsList] = useState<any[] | null>(null);
	const [activeHeadingItem, setActiveHeadingItem] = useState<null>(null);

	useEffect(() => {
		const getOddsType: CookieValueTypes = getCookie("UserPrefsCookie");

		if (getOddsType) {
			const oddsType: string[] = getOddsType.split("&");
			setGetUserPrefsCookie(oddsType);
		}
	}, []);

	useEffect(() => {
		setGetTime(formatTimeToCustomVersion(new Date(), false));
	}, []);

	useEffect(() => {
		if (data?.emit) {
			(async () => {
				const modifiedData = data?.emit.map(
					(obj: { e: string; et: string; fn: string; h: string; id: string; ti: string; __typename?: string }) => {
						delete obj["__typename"];
						return obj;
					},
				);
				const raw = JSON.stringify(modifiedData);
				const requestOptions: {
					method: string;
					headers: [];
					body: string;
					redirect: RequestRedirect | undefined;
				} = {
					method: "POST",
					headers: [],
					body: raw,
					redirect: "follow",
				};

				fetch(
					`${process.env.NEXT_PUBLIC_LOCAL_BASE || ""}/${publicRuntimeConfig.subdir}/${
						language ? language : "en"
					}/radioactivity/emit`,
					requestOptions,
				)
					.then(response => {
						if (!response.ok) {
							throw new Error("Radioactivity: " + response.statusText);
						}
						return response.json();
					})
					.then(data => {
						if (data.status !== "ok") {
							console.error("Radioactivity: " + data.message);
						}
					})
					.catch(error => {
						console.error(error);
					});
			})();
		}
	}, [data?.emit]);

	useEffect(() => {
		axios
			.get(
				`${process.env.NEXT_PUBLIC_LOCAL_BASE || ""}/${publicRuntimeConfig.subdir}/${
					language ? language : "en"
				}/api/v1/app_config`,
				{},
			)
			.then(res => {
				if (res?.data) {
					setGetErrorPageURL(res?.data[language]?.system?.site?.page["404"]);
					setGetPredictionsPageCode(res?.data[language]?.system?.site?.predictions_page);
					let getDomain: string;
					let getLogo: { [index: string]: string } = {};

					if (language === "zh-cn") {
						getDomain = res?.data["zh-hant"]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data["zh-hant"]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data["zh-hant"]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data["zh-hant"]?.system?.site?.predictions_page));
						setTrueLang("zh-hant");
					} else if (language === "zh-tw") {
						getDomain = res?.data["zh-hans"]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data["zh-hans"]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data["zh-hans"]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data["zh-hans"]?.system?.site?.predictions_page));
						setTrueLang("zh-hans");
					} else if (language === "de") {
						getDomain = res?.data["nl"]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data["nl"]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data["nl"]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data["nl"]?.system?.site?.predictions_page));
						setTrueLang("nl");
					} else if (language === "no") {
						getDomain = res?.data["no"]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data["nn"]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data["nn"]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data["nn"]?.system?.site?.predictions_page));
						setTrueLang("nn");
					} else if (language === "pt") {
						getDomain = res?.data["pt-pt"]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data["pt-pt"]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data["pt-pt"]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data["pt-pt"]?.system?.site?.predictions_page));
						setTrueLang("pt-pt");
					} else {
						getDomain = res?.data[language]?.br_haywire?.configurations?.base_url.split(".");
						getDomain = res?.data[language]?.br_haywire?.configurations?.base_url.split(".");
						getLogo = res?.data[language]?.domain_access_logo?.settings?.logo;
						setHaywireUrls(res?.data[language]?.br_haywire?.configurations);
						setPredictionNId(Number(res?.data[language]?.system?.site?.predictions_page));
						setTrueLang(language);
					}

					if (getLogo) {
						Object.keys(getLogo).forEach((item: string) => {
							let checkIcon: string;
							if (item.split("_").length >= 3) {
								checkIcon = `${item.split("_")[1]}_${item.split("_")[2]}`;
							} else {
								checkIcon = item.split("_")[1];
							}
							if (checkIcon === getDomain[getDomain.length - 1].replace("/", "")) {
								setHeaderLogo(getLogo[item]);
							}
						});
					}

					const newArr = [ShareMockData[0]];

					ShareMockData.map(item => {
						for (const val in res?.data[language]?.br_social?.configurations) {
							if (val === item?.value.toLowerCase()) newArr.push(item);
						}
					});

					setBrSocial(newArr);

					if (res.data[language]?.system?.site?.cookies_bar) {
						setCookieBannerUUID(res.data[language].system.site.cookies_bar);
					}
				}
			})
			.catch(e => console.log("e", e));
	}, [router]);

	useEffect(() => {
		axios
			.get(
				`${process.env.NEXT_PUBLIC_LOCAL_BASE || ""}/${publicRuntimeConfig.subdir}/${
					language ? language : "en"
				}/api/date-formats`,
				{},
			)
			.then(res => setDateFormats(res?.data))
			.catch(e => console.log("e", e));
	}, [router]);

	const handleGetSports = () => {
		axios
			.get(
				`${process.env.NEXT_PUBLIC_LOCAL_BASE || ""}/${publicRuntimeConfig.subdir}/${
					language ? language : "en"
				}/api/sports/${language ? language : "en"}`,
				{},
			)
			.then(res => {
				if (res?.data) {
					setSportsDataLoader(false);
					setSportsData(res?.data["Sports/Leagues"]);
				}
			})
			.catch(e => {
				setSportsDataLoader(false);
				console.log("e", e);
			});
	};

	useEffect(() => {
		if (sportsData) {
			let getAllSports: any = [];
			const getAllSportsLeagues: any[] = [];
			let getAllFeaturedSports: any = [];

			sportsData && (getAllSports = Object.keys(sportsData).map(item => sportsData[item]));

			sportsData &&
				Object.keys(sportsData).forEach(item => {
					if (sportsData[item]?.children !== undefined) {
						getAllSportsLeagues.push(
							...Object.keys(sportsData[item]?.children)
								.filter(i => sportsData[item].children[i]?.name)
								.filter(
									i =>
										!sportsData[item].children[i]?.name.includes("Corners") &&
										!sportsData[item].children[i]?.name.includes("Booking"),
								)
								.map(i => sportsData[item]?.children[i]),
						);
					}
				});

			sportsData && (getAllFeaturedSports = Object.keys(sportsData).map(item => sportsData[item]));
			getAllFeaturedSports &&
				(getAllFeaturedSports = Object.keys(getAllFeaturedSports).map(item => {
					const isFeatured: SportItemInterface[] = [];
					const noFeatured: SportItemInterface[] = [];

					if (getAllFeaturedSports[item]?.children) {
						Object.keys(getAllFeaturedSports[item]?.children).forEach(i => {
							if (
								getAllFeaturedSports[item].children[i]?.name &&
								!getAllFeaturedSports[item].children[i]?.name.includes("Corners") &&
								!getAllFeaturedSports[item].children[i]?.name.includes("Booking")
							) {
								getAllFeaturedSports[item].children[i].matchupCount = 0;

								if (getAllFeaturedSports[item]?.children[i]?.featured === "1") {
									isFeatured.push(getAllFeaturedSports[item]?.children[i]);
								} else {
									noFeatured.push(getAllFeaturedSports[item]?.children[i]);
								}
							}
						});

						return {
							...getAllFeaturedSports[item],
							children: [
								{
									id: 1,
									title: getAllFeaturedSports[item]?.name,
									acordeon: false,
									data: isFeatured.sort(
										(a: any, b) => a?.name && a?.name.toLowerCase().localeCompare(b?.name && b?.name.toLowerCase()),
									),
								},
								{
									id: 2,
									title: getAllFeaturedSports[item]?.name,
									acordeon: true,
									data: noFeatured.sort(
										(a: any, b) => a?.name && a?.name.toLowerCase().localeCompare(b?.name && b?.name.toLowerCase()),
									),
								},
							],
						};
					}
					return getAllFeaturedSports[item];
				}));

			setGetAllFeaturedSports(
				getAllFeaturedSports.sort(
					(a: SportItemInterface, b: SportItemInterface) =>
						a?.featured_order &&
						a?.featured_order.toLowerCase().localeCompare(b?.featured_order && b?.featured_order.toLowerCase()),
				),
			);
			setGetAllSportsData(getAllSports);
			setGetAllSportsLeagues(getAllSportsLeagues);
		}
	}, [sportsData]);

	useEffect(() => {
		(async () => {
			if (haywireUrls) {
				// Get token from url (for SSO with pinnacle mobile app)
				const params = window.location.search.substring(1)?.split("&") || [];
				const tokenParam = params.find(el => el.startsWith("token="))?.split("=");
				const token = tokenParam?.length && tokenParam[1];

				// Get token from cookies
				const cookieToken = getCookie("token");

				const requestToken = token || cookieToken;

				if (!requestToken) return;

				verifyArcSession(requestToken, haywireUrls, deviceUuid).then(response => {
					if (response?.token) {
						setUserInfo(response);
					} else {
						handleRemoveSession();
						setUserInfo(null);
					}
				});
				userPreferences(requestToken, haywireUrls, deviceUuid).then(response => {
					if (response) {
						// setSelectorOddsUserData(response?.data.find((x: any) => x.name === "Preferences:Odds"));
					}
				});
			}
		})();
	}, [haywireUrls]);

	useEffect(() => {
		if (getUserPrefsCookie && getUserPrefsCookie[1].split("=")[1]) {
			setSelectorOddsDataActive(selectorOddsData.find(i => i?.value === getUserPrefsCookie[1].split("=")[1]));
			setSelectorOddsData(
				selectorOddsData &&
					selectorOddsData.map(item => {
						item.active = false;
						if (item?.value === getUserPrefsCookie[1].split("=")[1]) {
							item.active = !item?.active;
						}
						return item;
					}),
			);
		} else {
			setSelectorOddsDataActive(selectorOddsData.find(i => i?.value === "american"));
			setSelectorOddsData(
				selectorOddsData &&
					selectorOddsData.map(item => {
						item.active = false;
						if (item?.id === 1) {
							item.active = !item?.active;
						}
						return item;
					}),
			);
		}
	}, [getUserPrefsCookie]);

	/*useEffect(() => {
	setIsDarkMode(data?.darkMode);
	});*/

	const handleToggle = (boolean: boolean) => setToggle(boolean);

	const handleClickChangeOddsType = async (id: number): Promise<void> => {
		setSelectorOddsData(
			selectorOddsData &&
				selectorOddsData.map(item => {
					item.active = false;
					if (item?.id === id) {
						item.active = !item?.active;
					}
					return item;
				}),
		);
		setSelectorOddsDataActive(selectorOddsData.find(i => i?.active));
		setToggle(!toggle);
		if (userInfo?.token) {
			if (haywireUrls) {
				await axios.post(
					`${haywireUrls?.identity_base_url}${haywireUrls?.identity_api_version}/preferences`,
					{
						"name": "Preferences:Odds",
						"value": `{"format":"${
							selectorOddsData[id - 1].value
						}","type":"singles","hoveredOdds":{"hovered":false,"format":"${selectorOddsData[id - 1].value}"}}`,
					},
					{
						headers: {
							"X-Session": userInfo?.token,
							"X-API-Key": haywireUrls?.identity_api_key || "",
							"X-Device-UUID": deviceUuid || haywireUrls?.device_uuid,
						},
					},
				);
			}
		}
		if (getUserPrefsCookie) {
			setCookie(
				"UserPrefsCookie",
				`${getUserPrefsCookie[0]}&priceStyle=${selectorOddsData[id - 1].value}&${getUserPrefsCookie[2]}&${
					getUserPrefsCookie[3]
				}&${getUserPrefsCookie[4]}`,
				1000,
			);
			setSelectorOddsDataActive(selectorOddsData.find(i => i?.value === selectorOddsData[id - 1].value));
		} else {
			setCookie(
				"UserPrefsCookie",
				`languageId=2&priceStyle=${selectorOddsData[id - 1].value}&linesTypeView=a&device=d&languageGroup=all`,
				1000,
			);
			setSelectorOddsDataActive(selectorOddsData.find(i => i?.value === selectorOddsData[id - 1].value));
		}
	};

	const handleRemoveSession = async () => {
		if (haywireUrls) {
			const cookieToken = getCookie("token");

			// eslint-disable-next-line camelcase
			const { base_url, identity_api_version, identity_session, identity_api_key, device_uuid } = haywireUrls;

			await axios
				// eslint-disable-next-line camelcase
				.delete(`${base_url}${identity_api_version}/${identity_session}/${cookieToken}`, {
					headers: {
						"X-Session": cookieToken || "",
						// eslint-disable-next-line camelcase
						"X-API-Key": identity_api_key || "",
						// eslint-disable-next-line camelcase
						"X-Device-UUID": deviceUuid || device_uuid,
					},
				})
				.then(response => {
					if (response?.data?.code === "OK") {
						deleteCookie("token", {
							path: "/",
							domain: `.pinnacle.${window.location?.host.split(".").pop()}`,
						});
						deleteCookie("custid", {
							path: "/",
							domain: `.pinnacle.${window.location?.host.split(".").pop()}`,
						});
						window.location.reload();
					}
				})
				.catch(e => {
					deleteCookie("token", { path: "/", domain: `.pinnacle.${window.location?.host.split(".").pop()}` });
					deleteCookie("custid", { path: "/", domain: `.pinnacle.${window.location?.host.split(".").pop()}` });
					console.log("error handleRemoveSession", e);
				});
		}
	};

	const handleGlobalReset = (e: ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		setReset(true);
		setTimeout(() => {
			setReset(false);
		}, 1000);
	};

	const handleModal = (bool: boolean) => setModalOpen(bool);

	const handleSpotLight = () => setSpotlightCheck(!spotlightCheck);

	const handleAutoPlayVideo = () => setAutoPlayVideo(!autoPlayVideo);

	const handleToast = (status: TypeOptions | undefined | "status", message: string) => {
		toast(() => <div dangerouslySetInnerHTML={{ __html: message }} />, {
			position: toast.POSITION.TOP_RIGHT,
			className: styles["toast-container"],
			type: status === "status" ? "success" : status,
		});
	};

	const handleCSR = async (uuid: string | undefined, type: string) => {
		const paragraphInfo = getParagraphInfo(type);
		if (typeof paragraphInfo?.query === "function") {
			const { data, loading, errors } = await apolloInit.query({
				query: gql`
                    query {
                        ${paragraphInfo.query(uuid, language).replace(/(\r\n|\n|\r)/gm, "")}
                    }
                `,
				context: {
					headers: {
						// ...requestHeaders,
					},
				},
			});
			setCSRLoader(loading);
			return {
				data,
				loading,
				errors,
			};
		}
	};

	const handleBaseHaywireUrl = (
		url: string,
		params: {
			moneylineOnly?: boolean;
			primaryOnly?: boolean;
			withSpecials?: boolean;
			withThreeWaySpecials?: boolean;
			brandId?: number;
		},
	): Promise<AxiosResponse> => {
		return axios.get(
			`${haywireUrls?.guest_mode === "1" ? haywireUrls?.guest_base_url : haywireUrls?.base_url}${
				haywireUrls?.api_version
			}/${url}`,
			{
				params,
				headers:
					haywireUrls?.guest_mode === "1"
						? {
								"X-API-Key": haywireUrls?.api_key || "",
								"X-Device-UUID": haywireUrls?.sports_test_uuid || "",
						  }
						: userInfo?.token
						? {
								"X-API-Key": haywireUrls?.api_key || "",
								"X-Device-UUID": deviceUuid || haywireUrls?.device_uuid,
								"X-Session": userInfo?.token || "",
						  }
						: {
								"X-API-Key": haywireUrls?.api_key || "",
								"X-Device-UUID": deviceUuid || haywireUrls?.device_uuid,
						  },
			},
		);
	};

	const handleTextFontSize = (fontSize: number) => {
		setTextFontSize(fontSize);
	};

	const handleHeadingsList = (item: any[]) => {
		const headingsNodes = Array.from(item)?.map((node: any) => node.innerText);
		setHeadingsList(headingsNodes);
	};

	const handleActiveHeadingItem = (item: any) => {
		setActiveHeadingItem(item);
	};

	useEffect(() => {
		if (language) {
			setLangCode(language);
		}
	}, [language]);

	return (
		<GlobalContext.Provider
			value={{
				boxesBottom,
				sidebar,
				language,
				brSocial,
				modalOpen,
				spotlightCheck,
				autoPlayVideo,
				reset,
				isDarkMode,
				userInfo,
				CSRLoader,
				cookieBannerUUID,
				isPhone,
				isComp,
				haywireUrls,
				getTime,
				getAllSportsData,
				getAllFeaturedSports,
				getAllSportsLeagues,
				dateFormats,
				predictionNId,
				headerLogo,
				selectorOddsDataActive,
				selectorOddsData,
				toggle,
				deviceUuid,
				apolloInit,
				randomId,
				trueLang,
				getSportsDataLoader,
				getErrorPageURL,
				tld,
				getPredictionsPageCode,
				langCode,
				textFontSize,
				headingsList,
				activeHeadingItem,
				handleGlobalReset,
				handleModal,
				handleAutoPlayVideo,
				handleToast,
				handleCSR,
				handleRemoveSession,
				handleClickChangeOddsType,
				handleToggle,
				handleSpotLight,
				handleGetSports,
				handleBaseHaywireUrl,
				handleTextFontSize,
				handleHeadingsList,
				handleActiveHeadingItem,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
};
